import { useEffect, useRef } from 'react';
import { X, Leaf } from 'lucide-react';
import { useDashboard } from '../../context/DashboardContext';
import { formatTimeRange } from '../../utils/carePhaseUtils';

/**
 * CareModal — State C: Pflege-Übersicht mit aktiven Pflegephasen.
 *
 * Features:
 * - role="dialog", aria-modal="true", aria-labelledby="modal-title"
 * - Focus-Trap: Tab bleibt innerhalb des Modals
 * - ESC und Overlay-Klick schliessen das Modal
 * - Premium-Box am Ende (Monetarisierungs-Trigger)
 */
const CareModal = () => {
  const { modalOpen, setModalOpen, carePhase } = useDashboard();
  const modalRef = useRef(null);

  const activePlants = carePhase?.activePlants ?? [];

  // Focus management: first focusable element gets focus on open
  useEffect(() => {
    if (!modalOpen || !modalRef.current) return;

    const focusable = getFocusableElements(modalRef.current);
    if (focusable.length > 0) {
      focusable[0].focus();
    }
  }, [modalOpen]);

  // ESC key handler
  useEffect(() => {
    if (!modalOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setModalOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, setModalOpen]);

  if (!modalOpen) return null;

  // Focus-Trap: cycle Tab / Shift+Tab within modal
  const handleTabKey = (e) => {
    if (e.key !== 'Tab') return;
    const focusable = getFocusableElements(modalRef.current);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setModalOpen(false);
    }
  };

  return (
    /* Overlay */
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- modal backdrop: Escape key handled by inner dialog */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={handleOverlayClick}
      aria-hidden={!modalOpen}
    >
      {/* Modal */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- role="dialog" needs focus-trap keyboard handling */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className="relative w-full max-w-[560px] mx-4 bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]"
        onKeyDown={handleTabKey}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <h2 id="modal-title" className="text-base font-bold text-gray-900">
            Pflege-Übersicht
          </h2>
          <button
            onClick={() => setModalOpen(false)}
            aria-label="Dialog schließen"
            className="p-1 text-gray-500 hover:text-gray-600 rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 px-6 pb-6">
          {/* Subtitle */}
          <p className="text-sm font-semibold text-gray-900 mb-4">
            Aktuell relevante Pflegephasen:
          </p>

          {/* Care Items */}
          {activePlants.length > 0 ? (
            <ul className="flex flex-col gap-0">
              {activePlants.map(({ plant: plantData, actions }, index) => {
                return (
                  <li
                    key={plantData.id ?? plantData.botanical_name ?? index}
                  >
                    {index > 0 && <div className="border-t border-gray-100" />}
                    <div className="flex items-start gap-3 py-3">
                      <Leaf
                        size={16}
                        className="text-emerald-500 mt-0.5 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-[13px] font-bold text-gray-900">
                          {plantData.name || plantData.scientific_name || 'Pflanze'}
                        </span>
                        {actions.length > 0 && (
                          <div className="flex flex-col gap-0.5 mt-1">
                            {actions.map((action, i) => (
                              <span
                                key={i}
                                className="text-xs text-gray-500"
                              >
                                {action.label}
                                {action.timeRange ? ` · ${formatTimeRange(action.timeRange)}` : ''}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-[13px] text-gray-500">
              Keine aktiven Pflegephasen gefunden.
            </p>
          )}

          {/* Separator before premium box */}
          <div className="border-t border-gray-100 mt-4 mb-4" />

          {/* Premium-Box (State C Conversion-Hook, #565).
              TODO(#565 / #323): "Erinnerungen aktivieren" ist bis zur Premium-/
              Zahlungs-Integration (#323) bewusst deaktiviert — kein toter CTA
              (vgl. #246 Landing-Page). Sobald Erinnerungen existieren: Button
              aktivieren, Opt-in verdrahten und den "bald verfügbar"-Hinweis
              entfernen. */}
          <div className="bg-emerald-50 rounded-lg p-4">
            <p className="text-[13px] text-gray-700 mb-1">
              Möchtest du daran erinnert werden, wenn im Garten etwas ansteht?
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Erinnerungen kommen mit Grünraum Premium – bald verfügbar.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                disabled
                aria-disabled="true"
                title="Erinnerungen werden mit Grünraum Premium verfügbar"
                className="w-full bg-emerald-500/50 text-white rounded-lg px-6 py-3 text-[13px] font-medium cursor-not-allowed"
              >
                Erinnerungen aktivieren
              </button>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="w-full text-gray-500 font-medium text-[13px] min-h-[44px] py-2 hover:text-gray-700 transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Returns all focusable elements within a container.
 */
function getFocusableElements(container) {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => !el.disabled && el.offsetParent !== null);
}

export default CareModal;
